import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {AppMessageService} from '@common-module/app-services/app-message.service';
import {UiSpinnerService} from '@common-module/app-services/ui-spinner.service';
import {DriverView} from '@common-module/modelinterfaces/driver-view.model';
import {DriverService} from '@common-module/services/driver.service';
import {DriverState} from '../../../../shared/constants/enums/driver-state.enum';
import {DriverManagementService} from '../../../../shared/services/driver-management.service';
import {SelectedService} from '../../../../shared/services/selected.service';
import {PermissionsSecurityService} from '../../../../shared/services/permissions-security.service';
import {FeatureKey} from '@common-module/modelinterfaces/enums/feature-key';
import {SearchComponent} from '@common-module/app-components/search/search.component';
import {TranslateModule} from '@ngx-translate/core';
import {MaterialModule} from '@common-module/material.module';
import {
  DriverListTableComponent,
} from './driver-list-table/driver-list-table.component';

@Component({
  selector: 'app-driver-list',
  templateUrl: './driver-list.component.html',
  styleUrls: ['./driver-list.component.scss', '../../../../../../../common-module/src/lib/app-styles/dialog-common.scss'],
  imports: [
    DriverListTableComponent,
    MaterialModule,
    SearchComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverListComponent implements OnInit {
  private readonly appMessageService = inject(AppMessageService);
  private readonly driverService = inject(DriverService);
  private readonly driverManagementService = inject(DriverManagementService);
  private readonly selectedService = inject(SelectedService);
  private readonly uiSpinnerService = inject(UiSpinnerService);
  private readonly permissionsSecurityService = inject(PermissionsSecurityService);

  protected readonly drivers = signal<DriverView[]>([]);
  protected readonly isListMode = signal(false);
  protected readonly lastSelectedUnitName = signal<string | undefined>(undefined);
  protected readonly search = signal('');
  protected readonly canEdit = signal(true);

  protected readonly filteredDrivers = computed(() => {
    const search = this.search().trim().toLowerCase();
    if (!search) {
      return this.drivers();
    }
    return this.drivers().filter(driver => (
      driver.getFullName().toLowerCase().includes(search)
      || driver.phoneNumber?.toLowerCase().includes(search)
      || driver.unit?.name?.toLowerCase().includes(search)
    ));
  });

  ngOnInit(): void {
    this.isListMode.set(
      this.driverManagementService.instantState() === DriverState.LIST,
    );
    this.getDriverList();
    this.lastSelectedUnitName.set(this.selectedService.getInstantUnit()?.name);
    this.canEdit.set(
      this.permissionsSecurityService.isAllowed(FeatureKey.DRIVERS_EDIT),
    );
  }

  private getDriverList(): void {
    this.uiSpinnerService.show();
    this.driverService.getList().subscribe(list => {
      this.uiSpinnerService.stop();
      this.driverManagementService.changeDriverList(list);
      if (!this.isListMode()) {
        list = list.filter(driver => driver.id !== this.driverManagementService.instantDriver()?.id);
      }
      this.drivers.set(list);
    });
  }

  protected onAddDriver(): void {
    this.driverManagementService.changeState(DriverState.ADD);
  }

  protected onOpenHistory(driver: DriverView): void {
    this.driverManagementService.openHistory(driver);
  }

  protected onEditDriver(driver: DriverView): void {
    this.driverManagementService.tempDriver = driver;
    this.driverManagementService.changeState(DriverState.EDIT);
  }

  protected onAssignDriver(driver: DriverView): void {
    this.driverManagementService.assignDriver(driver, driver.unit?.id);
  }

  protected onDeleteDriver(driver: DriverView): void {
    this.uiSpinnerService.show();
    this.driverService.delete(driver.id).subscribe(() => {
      this.uiSpinnerService.stop();
      this.appMessageService.openSnackBar('message.info.changes-saved');
      this.drivers.update(drivers => (
        drivers.filter(item => item.id !== driver.id)
      ));
    });
  }

  protected onClosePage(): void {
    this.driverManagementService.changeState(DriverState.DEFAULT);
  }
}
